/**
 * 成就系统 Store (Pinia)
 * 
 * 管理用户成就数据、等级、签到等功能
 * 
 * @version 1.0.0
 * @author Frontend Team
 * @since 2025-12-11
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import * as achievementAPI from '@/api/achievement'
import type {
  UserAchievementResponse
} from '@/types/achievement.types'

export const useAchievementStore = defineStore('achievement', () => {
  // ============= 状态定义 =============

  // 用户成就数据
  const achievementData = ref<UserAchievementResponse | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // 签到状态
  const hasCheckedInToday = ref(false)
  const isCheckingIn = ref(false)

  // ============= Getters =============

  /** 当前等级 */
  const level = computed(() => achievementData.value?.level ?? 1)

  /** 当前经验值 */
  const experience = computed(() => achievementData.value?.experience ?? 0)

  /** 下一级所需经验值 */
  const nextLevelExp = computed(() => achievementData.value?.nextLevelExp ?? 100)

  /** 等级进度百分比 */
  const levelProgress = computed(() => achievementData.value?.levelProgress ?? 0)

  /** 等级称号 */
  const title = computed(() => achievementData.value?.title ?? '新手')

  /** 累计总经验值 */
  const totalExp = computed(() => achievementData.value?.totalExp ?? 0)

  /** 统计数据 */
  const stats = computed(() => achievementData.value?.stats ?? {
    totalChars: 0,
    totalTasks: 0,
    totalDuration: 0,
    daysActive: 0,
    currentStreak: 0,
    maxStreak: 0,
    voicesUsed: 0,
    batchCount: 0,
    apiCount: 0
  })

  /** 已解锁徽章 */
  const unlockedBadges = computed(() => achievementData.value?.unlockedBadges ?? [])

  /** 未解锁徽章 */
  const lockedBadges = computed(() => achievementData.value?.lockedBadges ?? [])

  /** 最近解锁 */
  const recentUnlocks = computed(() => achievementData.value?.recentUnlocks ?? [])

  /** 当前连续签到天数 */
  const currentStreak = computed(() => stats.value.currentStreak)

  /** 是否已加载 */
  const isLoaded = computed(() => achievementData.value !== null)

  /** 今天是否已签到（根据后端数据计算） */
  const hasCheckedInTodayComputed = computed(() => {
    if (!achievementData.value?.stats?.lastCheckInAt) return false

    const lastCheckIn = new Date(achievementData.value.stats.lastCheckInAt)
    const today = new Date()

    // 判断是否是同一天
    return (
      lastCheckIn.getFullYear() === today.getFullYear() &&
      lastCheckIn.getMonth() === today.getMonth() &&
      lastCheckIn.getDate() === today.getDate()
    )
  })

  // ============= Actions =============

  /**
   * 获取我的成就数据
   */
  async function fetchMyAchievements(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const data = await achievementAPI.getMyAchievements()
      achievementData.value = data

      // 根据后端数据更新签到状态
      hasCheckedInToday.value = hasCheckedInTodayComputed.value
    } catch (err: any) {
      const errorMsg = err.message || '获取成就数据失败'
      error.value = errorMsg
      ElMessage.error(errorMsg)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 每日签到
   */
  async function checkIn(): Promise<void> {
    // 使用计算属性判断是否已签到
    if (hasCheckedInTodayComputed.value) {
      ElMessage.warning('今天已经签到过了')
      return
    }

    isCheckingIn.value = true

    try {
      const result = await achievementAPI.dailyCheckIn()
      
      if (result.success) {
        hasCheckedInToday.value = true
        ElMessage.success(`签到成功！获得 ${result.expGained} 经验值`)
        
        // 刷新成就数据
        await fetchMyAchievements()
      }
    } catch (err: any) {
      // 如果是已签到错误，静默处理
      if (err.message?.includes('已签到')) {
        hasCheckedInToday.value = true
      } else {
        ElMessage.error(err.message || '签到失败')
      }
      throw err
    } finally {
      isCheckingIn.value = false
    }
  }

  /**
   * 重置签到状态（用于测试或跨天重置）
   */
  function resetCheckInStatus(): void {
    hasCheckedInToday.value = false
  }

  /**
   * 清空数据
   */
  function clearData(): void {
    achievementData.value = null
    hasCheckedInToday.value = false
    error.value = null
  }

  // ============= 返回 =============

  return {
    // 状态
    achievementData,
    isLoading,
    error,
    hasCheckedInToday,
    isCheckingIn,

    // Getters
    level,
    experience,
    nextLevelExp,
    levelProgress,
    title,
    totalExp,
    stats,
    unlockedBadges,
    lockedBadges,
    recentUnlocks,
    currentStreak,
    isLoaded,

    // Actions
    fetchMyAchievements,
    checkIn,
    resetCheckInStatus,
    clearData
  }
})

